/**
 * "Correct this record" — how a dedupe collision or a wrong detail is resolved. Laid out around the
 * one thing an admin must not miss (§14.4): which system owns the field they are about to change.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx
 *   a settings form in titled sections with the save action in a footer row;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   the icon tile beside each section heading;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   the bordered, tinted callout panel each group sits in.
 *
 * Tally-owned and portal-owned fields are grouped apart and colour-coded, each group explaining its
 * rule once. `FieldOwnerBadge` still sits ON every field, inside the hint the input's
 * `aria-describedby` points at — the group heading is a summary, never a replacement (`010` FR-016).
 */
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  PiArrowsClockwiseDuotone as TallyIcon,
  PiChatCenteredTextDuotone as ReasonIcon,
  PiInfoDuotone as InfoIcon,
  PiStorefrontDuotone as PortalIcon,
} from "react-icons/pi";
import { Alert, Badge, Button, Text, Textarea, Title } from "rizzui";
import { FieldError, Field, FieldOwnerBadge, cn } from "@steelyard/theme";
import { CardTitle } from "../detail-parts";
import type { CustomerDetail } from "../../lib/services/customers";

const overrideSchema = z.object({
  legalName: z.string().trim().max(200),
  displayName: z.string().trim().max(200),
  gstin: z.string().trim().max(15),
  email: z.string().trim().max(320).email("Enter a valid email").optional().or(z.literal("")),
  registeredMobile: z.string().trim().max(20),
  reason: z.string().trim().min(1, "Required"),
});
type OverrideValues = z.infer<typeof overrideSchema>;

export function CustomerOverrideForm({
  onSubmit,
  customer,
  error,
}: {
  onSubmit: (formData: FormData) => void | Promise<void>;
  customer: CustomerDetail;
  error?: unknown;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OverrideValues>({
    resolver: zodResolver(overrideSchema),
    defaultValues: {
      legalName: customer.legalName,
      displayName: customer.summary.displayName,
      gstin: customer.gstin ?? "",
      email: customer.email ?? "",
      registeredMobile: customer.registeredMobile ?? "",
      reason: "",
    },
  });

  const submit = handleSubmit(async (values) => {
    const formData = new FormData();
    formData.set("legalName", values.legalName);
    formData.set("displayName", values.displayName);
    formData.set("gstin", values.gstin);
    formData.set("email", values.email ?? "");
    formData.set("registeredMobile", values.registeredMobile);
    formData.set("reason", values.reason);
    await onSubmit(formData);
  });

  return (
    <form onSubmit={submit} noValidate className="@container flex flex-col gap-5">
      {error ? <FieldError error={error} /> : null}

      <Group
        id="override-tally"
        tone="tally"
        icon={<TallyIcon aria-hidden className="size-5" />}
        title="Owned by Tally"
        chip="Override"
        summary="An edit here is a local override, not a fix: it needs a reason, flags this customer as diverging from the accounting record on the dedupe report, and the next Tally pull reapplies Tally's own value over it (contracts/customers-admin.md §8)."
      >
        {customer.divergesFromAccounting ? (
          <div role="alert" className="col-span-full">
            <Alert color="warning" variant="flat">
              This record already diverges from the accounting record. A further override adds to that divergence
              rather than resolving it.
            </Alert>
          </div>
        ) : null}
        <Field
          label="Legal name"
          maxLength={200}
          hint={
            <>
              <FieldOwnerBadge owner="tally" /> On record: {customer.legalName}
            </>
          }
          error={errors.legalName?.message}
          {...register("legalName")}
        />
        <Field
          label="GSTIN"
          maxLength={15}
          autoCapitalize="characters"
          hint={
            <>
              <FieldOwnerBadge owner="tally" /> On record: {customer.gstin ?? "none"}. Format is not checked here — only
              the GST portal can confirm a GSTIN is genuine.
            </>
          }
          error={errors.gstin?.message}
          {...register("gstin")}
        />
      </Group>

      <Group
        id="override-portal"
        tone="portal"
        icon={<PortalIcon aria-hidden className="size-5" />}
        title="Owned by the portal"
        chip="Normal edit"
        summary="These change for good. Tally never writes them — an inbound accounting update that tries is refused."
        wide
      >
        <Field
          label="Display name"
          maxLength={200}
          hint={
            <>
              <FieldOwnerBadge owner="portal" /> Refused on an inbound accounting update.
            </>
          }
          error={errors.displayName?.message}
          {...register("displayName")}
        />
        <Field
          label="Email"
          type="email"
          maxLength={320}
          placeholder="None on record"
          hint={
            <>
              <FieldOwnerBadge owner="portal" /> Refused on an inbound accounting update.
            </>
          }
          error={errors.email?.message}
          {...register("email")}
        />
        <Field
          label="Registered mobile"
          type="tel"
          maxLength={20}
          placeholder="None on record"
          hint={
            <>
              <FieldOwnerBadge owner="portal" /> Refused on an inbound accounting update.
            </>
          }
          error={errors.registeredMobile?.message}
          {...register("registeredMobile")}
        />
      </Group>

      <section aria-labelledby="override-reason-title" className="flex flex-col gap-3 rounded-xl border border-secondary/30 bg-secondary-lighter/20 p-4 @xl:p-5 dark:bg-secondary-lighter/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Title as="h4" id="override-reason-title" className="text-base font-semibold">
            <CardTitle icon={<ReasonIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
              Your reason
            </CardTitle>
          </Title>
          <Badge variant="flat" color="danger" rounded="pill" size="sm">
            Required
          </Badge>
        </div>
        <Textarea
          label="Why is this being changed?"
          rows={3}
          placeholder="e.g. Legal name corrected to match the GST certificate the customer sent on 12 Sept"
          helperText="Recorded immutably. Infra Junction refuses an override of a Tally-owned field without one, and a blank reason is useless six months later."
          error={errors.reason?.message}
          {...register("reason")}
        />
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-muted pt-5">
        <Text className="flex items-center gap-2 text-sm text-gray-500">
          <InfoIcon aria-hidden className="size-4 shrink-0 text-secondary" />
          Saving sends every field as shown here, with your reason.
        </Text>
        <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
          Save the correction
        </Button>
      </div>
    </form>
  );
}

/** One ownership group: a tinted panel, its rule stated once in the heading, its fields in a grid. */
function Group({
  id,
  tone,
  icon,
  title,
  chip,
  summary,
  wide,
  children,
}: {
  id: string;
  tone: "tally" | "portal";
  icon: ReactNode;
  title: string;
  chip: string;
  summary: string;
  /** Three columns on a wide card instead of two. */
  wide?: boolean;
  children: ReactNode;
}) {
  const tally = tone === "tally";
  return (
    <section
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col gap-4 rounded-xl border p-4 @xl:p-5",
        tally ? "border-orange/30 bg-orange-lighter/30 dark:bg-orange-lighter/10" : "border-blue/20 bg-blue-lighter/20 dark:bg-blue-lighter/10",
      )}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Title as="h4" id={`${id}-title`} className="text-base font-semibold">
            <CardTitle icon={icon} tile={tally ? "bg-orange-lighter text-orange-dark" : "bg-blue-lighter text-blue-dark"}>
              {title}
            </CardTitle>
          </Title>
          <Badge variant="flat" color={tally ? "warning" : "info"} rounded="pill" size="sm">
            {chip}
          </Badge>
        </div>
        <Text className="text-sm text-gray-600 @xl:ps-[52px]">{summary}</Text>
      </div>
      <div className={cn("grid gap-4 @xl:grid-cols-2", wide && "@3xl:grid-cols-3")}>{children}</div>
    </section>
  );
}
